import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface TrackingPixel {
  id: string;
  pixel_name: string;
  pixel_type: string;
  pixel_id: string;
  is_active: boolean;
  track_page_view: boolean;
  track_purchase: boolean;
  created_at: string;
  updated_at: string;
}

export function useTrackingPixels() {
  const queryClient = useQueryClient();

  const { data: pixels = [], isLoading } = useQuery({
    queryKey: ["tracking-pixels"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tracking_pixels")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as TrackingPixel[];
    },
  });

  const addPixel = useMutation({
    mutationFn: async (pixel: Omit<TrackingPixel, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("tracking_pixels")
        .insert(pixel)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tracking-pixels"] });
    },
  });

  const updatePixel = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<TrackingPixel> & { id: string }) => {
      const { error } = await supabase
        .from("tracking_pixels")
        .update(updates)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tracking-pixels"] });
    },
  });

  const deletePixel = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("tracking_pixels")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tracking-pixels"] });
    },
  });

  return {
    pixels,
    isLoading,
    addPixel,
    updatePixel,
    deletePixel,
  };
}

// Hook for active pixels only (used in frontend tracking)
export function useActiveTrackingPixels() {
  const { data: pixels = [], isLoading } = useQuery({
    queryKey: ["tracking-pixels-active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tracking_pixels")
        .select("*")
        .eq("is_active", true);

      if (error) throw error;
      return data as TrackingPixel[];
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  return { pixels, isLoading };
}
